/**
 * Material-UI theme configuration with RTL support for Arabic
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';

// Define custom colors for confidence levels and message types
declare module '@mui/material/styles' {
  interface Palette {
    confidence: {
      veryHigh: string;
      high: string;
      medium: string;
      low: string;
    };
    messageType: {
      title: string;
      media: string;
      album: string;
      separator: string;
      noise: string;
      replyStub: string;
      unknown: string;
    };
  }
  interface PaletteOptions {
    confidence?: {
      veryHigh: string;
      high: string;
      medium: string;
      low: string;
    };
    messageType?: {
      title: string;
      media: string;
      album: string;
      separator: string;
      noise: string;
      replyStub: string;
      unknown: string;
    };
  }
}

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2', // Blue - professional
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4caf50', // Green - for accept actions
      light: '#66bb6a',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f44336', // Red - for reject/delete
      light: '#ef5350',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800', // Orange
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000000',
    },
    info: {
      main: '#2196f3', // Light blue
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    success: {
      main: '#4caf50', // Green
      light: '#66bb6a',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
    // Custom confidence colors
    confidence: {
      veryHigh: '#4caf50', // Green (0.85-1.0)
      high: '#fbc02d', // Yellow (0.70-0.85)
      medium: '#ff9800', // Orange (0.50-0.70)
      low: '#f44336', // Red (<0.50)
    },
    // Custom message type colors
    messageType: {
      title: '#2196f3', // Blue
      media: '#9c27b0', // Purple
      album: '#673ab7', // Deep purple
      separator: '#9e9e9e', // Gray
      noise: '#757575', // Dark gray
      replyStub: '#bdbdbd', // Light gray
      unknown: '#ff9800', // Orange/warning
    },
  },
  typography: {
    fontFamily: [
      'Roboto', // English
      'Noto Sans Arabic', // Arabic
      'Cairo', // Arabic alternative
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.6,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.7,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        },
      },
    },
  },
};

// Create theme with LTR (default)
export const theme = createTheme(themeOptions);

// Create theme with RTL for Arabic
export const themeRTL = createTheme({
  ...themeOptions,
  direction: 'rtl',
});

// Helper function to get theme based on language
export const getTheme = (language: 'en' | 'ar' = 'en') => {
  return language === 'ar' ? themeRTL : theme;
};

export default theme;
